import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { 
  FileText, Plus, Send, CheckCircle, Clock, Award, AlertCircle,
  GraduationCap, Loader2, Calendar, BookOpen
} from 'lucide-react';
import api, { applicationsAPI, collegesAPI } from '../utils/api';

const GradeReports = () => {
  const [reportTypes, setReportTypes] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [applications, setApplications] = useState([]);
  const [colleges, setColleges] = useState({});
  const [reportStatus, setReportStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedApps, setSelectedApps] = useState([]);

  const [newReport, setNewReport] = useState({
    report_type: 'mid_year',
    school_year: '2024-2025',
    gpa: '',
    courses: [],
    notes: ''
  });

  const [newCourse, setNewCourse] = useState({ course_name: '', grade: '', credits: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [typesRes, reportsRes, statusRes, appsRes] = await Promise.all([
        api.get('/grades/report-types'),
        api.get('/grades/my-reports'),
        api.get('/grades/status'),
        applicationsAPI.getAll()
      ]);

      setReportTypes(typesRes.data.report_types || []);
      setMyReports(reportsRes.data.reports || []);
      setReportStatus(statusRes.data.applications || []);
      setApplications(appsRes);

      const collegeMap = {};
      for (const app of appsRes) {
        if (app.college_id && !collegeMap[app.college_id]) {
          try {
            const college = await collegesAPI.getById(app.college_id);
            collegeMap[app.college_id] = college;
          } catch {
            collegeMap[app.college_id] = { name: 'Unknown' };
          }
        }
      }
      setColleges(collegeMap);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddReport = async () => {
    try {
      await api.post('/grades/add', {
        ...newReport,
        gpa: parseFloat(newReport.gpa) || null
      });
      toast({ title: 'Success', description: 'Grade report saved successfully' });
      setShowAddDialog(false);
      setNewReport({ report_type: 'mid_year', school_year: '2024-2025', gpa: '', courses: [], notes: '' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save report', variant: 'destructive' });
    }
  };

  const handleAddCourse = () => {
    if (newCourse.course_name && newCourse.grade) {
      setNewReport({
        ...newReport,
        courses: [...newReport.courses, { ...newCourse, credits: parseFloat(newCourse.credits) || 0 }]
      });
      setNewCourse({ course_name: '', grade: '', credits: '' });
    }
  };

  const handleSubmitReport = async (reportType) => {
    if (selectedApps.length === 0) {
      toast({ title: 'Error', description: 'Select at least one college', variant: 'destructive' });
      return;
    }
    try {
      await api.post(`/grades/submit?report_type=${reportType}`, { application_ids: selectedApps });
      toast({ title: 'Success', description: 'Report submitted to selected colleges' });
      setSelectedApps([]);
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    }
  };

  const getCollegeName = (collegeId) => colleges[collegeId]?.name || 'Unknown';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Grade Reports</h1>
          <p className="text-gray-600">Submit mid-year and final grade reports to your colleges</p>
        </div>

        <Tabs defaultValue="reports">
          <TabsList className="mb-6">
            <TabsTrigger value="reports">My Reports</TabsTrigger>
            <TabsTrigger value="submit">Submit Reports</TabsTrigger>
            <TabsTrigger value="status">Status</TabsTrigger>
          </TabsList>

          <TabsContent value="reports">
            <div className="flex justify-end mb-4">
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Report
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add Grade Report</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Report Type</Label>
                        <Select value={newReport.report_type} onValueChange={(v) => setNewReport({...newReport, report_type: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {reportTypes.map(type => (
                              <SelectItem key={type.code} value={type.code}>{type.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>School Year</Label>
                        <Input value={newReport.school_year} onChange={(e) => setNewReport({...newReport, school_year: e.target.value})} />
                      </div>
                    </div>
                    <div>
                      <Label>Current GPA</Label>
                      <Input type="number" step="0.01" placeholder="e.g., 3.85" value={newReport.gpa} onChange={(e) => setNewReport({...newReport, gpa: e.target.value})} />
                    </div>
                    <div>
                      <Label>Courses (Optional)</Label>
                      <div className="flex gap-2 mb-2">
                        <Input placeholder="Course name" value={newCourse.course_name} onChange={(e) => setNewCourse({...newCourse, course_name: e.target.value})} />
                        <Input placeholder="Grade" className="w-20" value={newCourse.grade} onChange={(e) => setNewCourse({...newCourse, grade: e.target.value})} />
                        <Button type="button" variant="outline" onClick={handleAddCourse}>Add</Button>
                      </div>
                      {newReport.courses.length > 0 && (
                        <div className="text-sm text-gray-600">
                          {newReport.courses.map((c, i) => <span key={i} className="mr-2">{c.course_name}: {c.grade}</span>)}
                        </div>
                      )}
                    </div>
                    <Button onClick={handleAddReport} className="w-full">Save Report</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {myReports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No grade reports added yet</p>
                  <Button onClick={() => setShowAddDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Report
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myReports.map((report, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-blue-100 p-3 rounded-lg">
                            <FileText className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {reportTypes.find(t => t.code === report.report_type)?.name || report.report_type}
                            </h3>
                            <p className="text-sm text-gray-500">{report.school_year}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {report.gpa && <p className="text-2xl font-bold text-blue-600">{report.gpa.toFixed(2)} GPA</p>}
                          <Badge variant={report.status === 'submitted' ? 'default' : 'secondary'}>{report.status}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="submit">
            <Card>
              <CardHeader>
                <CardTitle>Submit Grade Reports</CardTitle>
                <CardDescription>Select colleges to send your grade reports</CardDescription>
              </CardHeader>
              <CardContent>
                {myReports.length === 0 ? (
                  <p className="text-gray-500">Add a grade report first before submitting.</p>
                ) : (
                  <div className="space-y-6">
                    {myReports.map((report, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold">{reportTypes.find(t => t.code === report.report_type)?.name}</h4>
                          <Badge>{report.school_year}</Badge>
                        </div>
                        <div className="space-y-2 mb-4">
                          {applications.map(app => (
                            <div key={app._id} className="flex items-center space-x-3 p-2 border rounded">
                              <Checkbox 
                                checked={selectedApps.includes(app._id)}
                                onCheckedChange={(checked) => {
                                  if (checked) setSelectedApps([...selectedApps, app._id]);
                                  else setSelectedApps(selectedApps.filter(id => id !== app._id));
                                }}
                              />
                              <GraduationCap className="h-4 w-4 text-gray-400" />
                              <span>{getCollegeName(app.college_id)}</span>
                            </div>
                          ))}
                        </div>
                        <Button onClick={() => handleSubmitReport(report.report_type)} disabled={selectedApps.length === 0}>
                          <Send className="h-4 w-4 mr-2" />
                          Submit to {selectedApps.length} College{selectedApps.length !== 1 ? 's' : ''}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="status">
            <Card>
              <CardHeader>
                <CardTitle>Report Status by College</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reportStatus.map((status, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">{status.college_name}</span>
                        <Badge variant={status.application_status === 'submitted' ? 'default' : 'secondary'}>
                          {status.application_status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          {status.mid_year_submitted ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-amber-500" />
                          )}
                          <span>Mid-Year Report</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {status.final_submitted ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-amber-500" />
                          )}
                          <span>Final Report</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GradeReports;
